/**
 * Permissions Helper Utilities
 * Centralized functions for checking user permissions and roles
 */

import { DS_F2W_Business_Travel_Team, DS_F2W_Edit, DS_F2W_HR_Team } from 'Constants/F2wRolesConstants';

/**
 * Check if user has specific role
 * @param {Array} userRoles - Array of user roles
 * @param {string} requiredRole - Required role to check
 * @returns {boolean} True if user has the role
 */
export const hasRole = (userRoles, requiredRole) => {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  return userRoles.includes(requiredRole);
};

/**
 * Check if user has any of the specified roles
 * @param {Array} userRoles - Array of user roles
 * @param {Array} requiredRoles - Array of required roles
 * @returns {boolean} True if user has any of the roles
 */
export const hasAnyRole = (userRoles, requiredRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  return requiredRoles.some((role) => userRoles.includes(role));
};

/**
 * Check if user has all specified roles
 * @param {Array} userRoles - Array of user roles
 * @param {Array} requiredRoles - Array of required roles
 * @returns {boolean} True if user has all roles
 */
export const hasAllRoles = (userRoles, requiredRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  return requiredRoles.every((role) => userRoles.includes(role));
};

/**
 * Check if user can edit ticket based on status and role
 * @param {Array} userRoles - Array of user roles
 * @param {string} ticketStatus - Current ticket status
 * @returns {boolean} True if user can edit
 */
export const canEditTicket = (userRoles, ticketStatus) => {
  const status = ticketStatus?.toLowerCase();

  if (hasRole(userRoles, DS_F2W_Business_Travel_Team)) {
    return ['confirmed', 'pendingdes', 'pendingbtt'].includes(status);
  }

  if (hasRole(userRoles, DS_F2W_Edit)) {
    return ['new', 'pendingdes', 'confirmed'].includes(status);
  }

  if (hasRole(userRoles, DS_F2W_HR_Team)) {
    return status === 'pendinghr';
  }

  return false;
};

/**
 * Check if user can delete ticket
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} True if user can delete
 */
export const canDeleteTicket = (userRoles) => {
  return hasRole(userRoles, DS_F2W_Business_Travel_Team);
};

/**
 * Check if user can access settings
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} True if user can access settings
 */
export const canAccessSettings = (userRoles) => {
  return hasRole(userRoles, DS_F2W_Business_Travel_Team);
};

/**
 * Check if user can generate reports
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} True if user can generate reports
 */
export const canGenerateReports = (userRoles) => {
  return hasAnyRole(userRoles, [DS_F2W_Business_Travel_Team, DS_F2W_Edit, DS_F2W_HR_Team]);
};

/**
 * Check if user can download PDF
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} True if user can download PDF
 */
export const canDownloadPdf = (userRoles) => {
  return hasRole(userRoles, DS_F2W_Business_Travel_Team);
};

/**
 * Check if user can add manual ticket
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} True if user can add manual ticket
 */
export const canAddManualTicket = (userRoles) => {
  return hasRole(userRoles, DS_F2W_Edit);
};

/**
 * Check if user can view Business Travel Team tab
 * @param {Array} userRoles - Array of user roles
 * @param {string} ticketStatus - Current ticket status
 * @returns {boolean} True if user can view tab
 */
export const canViewBusinessTravelTeamTab = (userRoles, ticketStatus) => {
  const status = ticketStatus?.toLowerCase();

  if (hasRole(userRoles, DS_F2W_Business_Travel_Team)) {
    return true;
  }

  if (hasAnyRole(userRoles, [DS_F2W_Edit, DS_F2W_HR_Team]) && status === 'confirmed') {
    return true;
  }

  return false;
};

/**
 * Check if user can view History tab
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} True if user can view history
 */
export const canViewHistoryTab = (userRoles) => {
  return hasRole(userRoles, DS_F2W_Business_Travel_Team);
};

/**
 * Check if user can view Ticket Info tab
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} True if user can view ticket info
 */
export const canViewTicketInfoTab = (userRoles) => {
  return hasRole(userRoles, DS_F2W_Business_Travel_Team);
};

/**
 * Check if user can change ticket status
 * @param {Array} userRoles - Array of user roles
 * @param {string} ticketStatus - Current ticket status
 * @returns {boolean} True if user can change status
 */
export const canChangeTicketStatus = (userRoles, ticketStatus) => {
  const status = ticketStatus?.toLowerCase();

  if (hasRole(userRoles, DS_F2W_Edit) && ['pendingdes', 'confirmed'].includes(status)) {
    return true;
  }

  if (hasRole(userRoles, DS_F2W_Business_Travel_Team) && ['confirmed', 'pendingdes', 'pendingbtt'].includes(status)) {
    return true;
  }

  return false;
};

/**
 * Check if user can view team performance section on dashboard
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} True if user can view team performance
 */
export const canViewTeamPerformance = (userRoles) => {
  return hasRole(userRoles, DS_F2W_Business_Travel_Team);
};

/**
 * Check if user can export dashboard as PDF
 * @param {Array} userRoles - Array of user roles
 * @returns {boolean} True if user can export dashboard PDF
 */
export const canExportDashboardPdf = (userRoles) => {
  return hasRole(userRoles, DS_F2W_Business_Travel_Team);
};

/**
 * Check if user can submit ticket
 * @param {Array} userRoles - Array of user roles
 * @param {string} ticketStatus - Current ticket status
 * @returns {boolean} True if user can submit
 */
export const canSubmitTicket = (userRoles, ticketStatus) => {
  const status = ticketStatus?.toLowerCase();

  if (hasRole(userRoles, DS_F2W_HR_Team) && status === 'pendinghr') {
    return true;
  }

  if (hasRole(userRoles, DS_F2W_Edit) && ['new', 'pendingdes', 'confirmed'].includes(status)) {
    return true;
  }

  if (hasRole(userRoles, DS_F2W_Business_Travel_Team) && ['pendingdes', 'pendingbtt', 'confirmed'].includes(status)) {
    return true;
  }

  return false;
};
